import React from 'react';
import { motion } from 'framer-motion';
import { Clock, User, ArrowRight } from 'lucide-react';

const BlogPage = () => {
  const articles = [
    {
      title: "The Art of Haute Couture",
      excerpt: "Exploring the meticulous craftsmanship and artistic vision behind creating bespoke fashion pieces.",
      image: "https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      author: "Isabella Chen",
      date: "March 15, 2024",
      category: "Fashion Design"
    },
    {
      title: "Sustainable Fashion: The Future of Design",
      excerpt: "How eco-conscious materials and ethical practices are reshaping the fashion industry.",
      image: "https://images.unsplash.com/photo-1581375074612-d1fd0e661aeb?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      author: "Marcus Thompson",
      date: "March 10, 2024",
      category: "Sustainability"
    },
    {
      title: "Innovation in Textile Design",
      excerpt: "Breaking boundaries with cutting-edge fabric technologies and artistic textile development.",
      image: "https://images.unsplash.com/photo-1544441892-794166f1e3be?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      author: "Sofia Rodriguez",
      date: "March 5, 2024",
      category: "Innovation"
    }
  ];

  const featuredArticle = {
    title: "The Evolution of Fashion Design in 2024",
    excerpt: "An in-depth look at how technology, sustainability, and cultural influences are shaping modern fashion design.",
    image: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80",
    author: "Isabella Chen",
    date: "March 20, 2024",
    category: "Industry Insights"
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-20"
    >
      {/* Featured Article */}
      <section className="relative h-[70vh]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${featuredArticle.image})` }}
        >
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="inline-block bg-emerald-400 text-black px-4 py-1 rounded-full text-sm font-semibold mb-4">
                {featuredArticle.category}
              </span>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">{featuredArticle.title}</h1>
              <p className="text-xl text-gray-300 mb-8">{featuredArticle.excerpt}</p>
              <div className="flex items-center text-gray-400">
                <User className="h-5 w-5 mr-2" />
                <span className="mr-4">{featuredArticle.author}</span>
                <Clock className="h-5 w-5 mr-2" />
                <span>{featuredArticle.date}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12">
            {articles.map((article, index) => (
              <article key={index} className="group">
                <div className="relative overflow-hidden rounded-lg mb-6">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-64 object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <span className="text-emerald-400 text-sm">{article.category}</span>
                <h3 className="text-2xl font-bold mt-2 mb-4 group-hover:text-emerald-400 transition-colors">
                  {article.title}
                </h3>
                <p className="text-gray-400 mb-4">{article.excerpt}</p>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center text-gray-400">
                    <User className="h-4 w-4 mr-2" />
                    <span className="mr-4">{article.author}</span>
                    <Clock className="h-4 w-4 mr-2" />
                    <span>{article.date}</span>
                  </div>
                  <button className="flex items-center text-emerald-400 hover:text-emerald-300 transition-colors">
                    Read More <ArrowRight className="h-4 w-4 ml-2" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-24 bg-black/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Stay Updated with Fashion Trends</h2>
            <p className="text-gray-400 mb-8">
              Subscribe to our newsletter for the latest updates on fashion design, sustainable practices, and industry insights.
            </p>
            <form className="flex gap-4">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-emerald-400"
              />
              <button className="px-6 py-3 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors">
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default BlogPage;